<template>
  <!--
    ProjectEditor — the Project_Editor create/edit form (task 13.3).

    Presents the editable project fields (title, description, Service_Category,
    Client_Record, show-on-website), plus image management (hosted URL or file
    upload) with alt text and a live preview. In edit mode the fields are
    populated from the project's current stored values; in create mode the form
    starts empty (Requirements 4.1, 4.2, 5.1, 7.1, 7.2, 7.9).

    Client-side validation blocks submission and marks the offending fields
    without sending a request (Requirements 4.4, 5.3). A missing-alt-text
    warning is surfaced but never blocks the save (Requirement 7.10). On a
    server error the entered values are preserved for correction (Requirements
    4.6, 5.6).

    Cross-cutting concerns are delegated to the container: success/error
    snackbars live in the ProjectManager container (task 14.1), which listens
    for the `saved` / `save-error` events. Unsaved-changes navigation guards
    (task 13.4) are installed here: an in-component `beforeRouteLeave` guard and
    a window `beforeunload` listener prompt before discarding edits, and the
    dirty flag is cleared on a successful save (Requirement 11). The component
    also exposes its dirty state via the `dirty-change` event and the `isDirty`
    computed.
  -->
  <v-card class="project-editor" :loading="loading">
    <v-card-title class="project-editor__title">
      {{ isEditMode ? 'Edit project' : 'Add project' }}
    </v-card-title>

    <v-divider />

    <!-- Load-failure state for edit mode (the project could not be fetched). -->
    <v-card-text v-if="loadError">
      <v-alert type="error" variant="tonal" class="mb-4">
        {{ loadError }}
      </v-alert>
      <v-btn color="#e7511e" variant="text" @click="reload">Retry</v-btn>
    </v-card-text>

    <v-card-text v-else>
      <!-- Server-side save error (values are preserved for correction). -->
      <v-alert
        v-if="saveError"
        type="error"
        variant="tonal"
        class="mb-4"
        data-test="save-error"
      >
        {{ saveError }}
      </v-alert>

      <v-form ref="form" @submit.prevent="save">
        <v-text-field
          v-model="title"
          label="Title"
          color="#e7511e"
          :counter="MAX_TITLE_LENGTH"
          :maxlength="MAX_TITLE_LENGTH"
          :error="!!fieldErrors.title"
          :error-messages="fieldErrors.title || []"
          data-test="title"
          @update:model-value="clearFieldError('title')"
        />

        <v-textarea
          v-model="description"
          label="Description"
          color="#e7511e"
          hint="Brief description of the project"
          :counter="MAX_DESCRIPTION_LENGTH"
          :error="!!fieldErrors.description"
          :error-messages="fieldErrors.description || []"
          rows="3"
          auto-grow
          data-test="description"
          @update:model-value="clearFieldError('description')"
        />

        <v-autocomplete
          v-model="projectTypeIds"
          :items="projectTypes"
          item-title="type"
          item-value="id"
          label="Service categories"
          multiple
          chips
          closable-chips
          color="#e7511e"
          :loading="referencesLoading"
          :error="!!fieldErrors.project_type_ids"
          :error-messages="fieldErrors.project_type_ids || []"
          data-test="service-category"
          @update:model-value="clearFieldError('project_type_ids')"
        />

        <v-autocomplete
          v-model="clientId"
          :items="clients"
          item-title="name"
          item-value="id"
          label="Client"
          color="#e7511e"
          :loading="referencesLoading"
          :error="!!fieldErrors.client_id"
          :error-messages="fieldErrors.client_id || []"
          data-test="client"
          @update:model-value="clearFieldError('client_id')"
        />

        <v-switch
          v-model="showOnWebsite"
          inset
          color="#e7511e"
          label="Show on website"
          data-test="show-on-website"
        />

        <v-divider class="my-4" />

        <div class="text-subtitle-1 mb-2">Project image</div>

        <!-- Choose how the image is supplied: a hosted URL or a file upload. -->
        <v-btn-toggle
          v-model="imageMode"
          mandatory
          color="#e7511e"
          variant="outlined"
          density="comfortable"
          class="mb-4"
          data-test="image-mode"
        >
          <v-btn value="url">Image URL</v-btn>
          <v-btn value="upload">Upload file</v-btn>
        </v-btn-toggle>

        <v-text-field
          v-if="imageMode === 'url'"
          v-model="imageUrl"
          label="Image URL"
          color="#e7511e"
          placeholder="https://…"
          :maxlength="MAX_IMAGE_URL_LENGTH"
          :error="!!fieldErrors.image"
          :error-messages="fieldErrors.image || []"
          clearable
          data-test="image-url"
          @update:model-value="onImageUrlInput"
        />

        <v-file-input
          v-else
          v-model="imageFile"
          label="Upload image (JPEG, PNG, or WebP, max 5 MB)"
          color="#e7511e"
          accept="image/jpeg,image/png,image/webp"
          prepend-icon="mdi-image"
          :error="!!fieldErrors.image"
          :error-messages="fieldErrors.image || []"
          show-size
          data-test="image-file"
          @update:model-value="onFileSelected"
        />

        <v-text-field
          v-model="imageAlt"
          label="Image alt text"
          color="#e7511e"
          hint="Describe the image for accessibility"
          data-test="image-alt"
        />

        <!-- Missing-alt-text warning: informs but never blocks the save
             (Requirement 7.10). -->
        <v-alert
          v-if="showAltWarning"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-4"
          data-test="alt-warning"
        >
          Alternative text improves accessibility. You can still save without it.
        </v-alert>

        <!-- Current image preview (Requirement 7.9). -->
        <div v-if="previewUrl" class="project-editor__preview mb-4">
          <div class="text-caption mb-1">Preview</div>
          <v-img
            :src="previewUrl"
            :alt="imageAlt"
            max-height="200"
            max-width="320"
            class="project-editor__preview-img"
            data-test="image-preview"
          />
          <v-btn
            variant="text"
            color="error"
            size="small"
            class="mt-2"
            prepend-icon="mdi-delete"
            data-test="remove-image"
            @click="requestRemoveImage"
          >
            Remove image
          </v-btn>
        </div>

        <!-- Confirmation that the image will be removed on save. -->
        <v-alert
          v-else-if="removeRequested && hadImageInitially"
          type="info"
          variant="tonal"
          density="compact"
          class="mb-4"
          data-test="image-removed-notice"
        >
          The image will be removed when you save.
        </v-alert>

        <v-divider class="my-4" />

        <div class="d-flex ga-3 justify-end">
          <v-btn
            variant="text"
            :disabled="saving"
            data-test="cancel"
            @click="cancel"
          >
            Cancel
          </v-btn>
          <v-btn
            type="submit"
            color="#e7511e"
            :loading="saving"
            :disabled="loading"
            data-test="save"
          >
            {{ isEditMode ? 'Save changes' : 'Create project' }}
          </v-btn>
        </div>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
// ProjectEditor — Project_Editor create/edit form (task 13.3).
import AdminProjectApi from 'services/AdminProjectApi'
import AdministrationApi from 'services/AdministrationApi'
import { captureBaseline, isDirty } from 'services/dirtyTracking'

// Field limits mirror the server-side validators in
// server/routes/administration/projectValidation.js so the client blocks
// obviously-invalid submissions before a request is sent. The server remains
// the source of truth (Requirements 4.4, 5.3, 12.1, 12.4).
const MAX_TITLE_LENGTH = 200
const MAX_DESCRIPTION_LENGTH = 5000
const MAX_IMAGE_URL_LENGTH = 2048

// A well-formed http/https URL for the hosted-image path (Requirement 7.5).
function isValidHttpUrl (value) {
  if (typeof value !== 'string') return false
  if (value.length > MAX_IMAGE_URL_LENGTH) return false
  let parsed
  try {
    parsed = new URL(value)
  } catch (e) {
    return false
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:'
}

// True when a selector value refers to a real selection (not blank/absent).
function isSelected (value) {
  return value !== null && value !== undefined && value !== ''
}

// Best-effort extraction of a human-readable message from an API error while
// never leaking internal detail (the server sends generic messages).
function extractErrorMessage (err, fallback) {
  const data = err && err.response && err.response.data
  if (data) {
    if (typeof data.message === 'string' && data.message) return data.message
    if (typeof data.error === 'string' && data.error) return data.error
  }
  return fallback
}

export default {
  name: 'ProjectEditor',

  props: {
    // Project id for edit mode. When absent the editor is in create mode.
    // The admin route (task 14.2) passes this as a route prop.
    id: {
      type: [String, Number],
      default: null
    }
  },

  emits: ['saved', 'save-error', 'dirty-change', 'cancel'],

  data () {
    return {
      MAX_TITLE_LENGTH,
      MAX_DESCRIPTION_LENGTH,
      MAX_IMAGE_URL_LENGTH,

      // Editable fields.
      title: '',
      description: '',
      projectTypeIds: [],
      clientId: null,
      showOnWebsite: true,

      // Image management.
      imageMode: 'url', // 'url' | 'upload'
      imageUrl: '',
      imageFile: null,
      imageAlt: '',
      // True when the admin has explicitly asked to remove the existing image.
      // Distinguishes "keep the stored (uploaded) image" — the URL field is
      // legitimately empty for uploads — from an intentional removal on save.
      removeRequested: false,

      // Reference data for the selectors (Requirement 4.2).
      projectTypes: [],
      clients: [],

      // Baseline of stored image state (edit mode) used to decide whether an
      // image request is needed and to build the preview.
      initialImageReference: '',
      initialImageUrl: '',
      initialImageAlt: '',
      hadImageInitially: false,

      // Object URL for previewing a freshly selected upload; revoked on change.
      uploadPreviewUrl: '',

      // Status flags.
      referencesLoading: false,
      projectLoading: false,
      saving: false,
      loadError: '',
      saveError: '',

      // Validation state; keyed by field, populated on a blocked submit.
      fieldErrors: {},

      // Unsaved-changes baseline (dirtyTracking).
      baseline: null,
      lastDirty: false
    }
  },

  computed: {
    // Resolve the project id from the prop, falling back to the route param so
    // the component works whether or not it is registered with route props.
    projectId () {
      if (this.id !== null && this.id !== undefined && this.id !== '') return this.id
      const route = this.$route
      return route && route.params ? route.params.id : null
    },

    isEditMode () {
      return isSelected(this.projectId)
    },

    loading () {
      return this.referencesLoading || this.projectLoading
    },

    // True when an image is present in the form (a chosen upload, a typed URL,
    // or an already-stored image in edit mode). A pending removal with no new
    // input means there is effectively no image.
    hasImage () {
      if (this.removeRequested && !this.imageFile && (this.imageUrl || '').trim() === '') {
        return false
      }
      if (this.imageMode === 'upload') {
        return !!this.imageFile || this.hadImageInitially
      }
      return (this.imageUrl || '').trim() !== '' || this.hadImageInitially
    },

    // Warn (without blocking) when an image is set but alt text is missing
    // (Requirement 7.10).
    showAltWarning () {
      return this.hasImage && (this.imageAlt || '').trim() === ''
    },

    // The image to show in the preview: a freshly selected upload takes
    // precedence, then a typed URL, then the stored image's served URL
    // (initialImageUrl) so an existing uploaded image previews even though its
    // internal key is not shown in the URL field. A pending removal hides it
    // (Requirement 7.9).
    previewUrl () {
      if (this.imageMode === 'upload') {
        if (this.uploadPreviewUrl) return this.uploadPreviewUrl
        return this.removeRequested ? '' : (this.initialImageUrl || '')
      }
      const url = (this.imageUrl || '').trim()
      if (url) return url
      return this.removeRequested ? '' : (this.initialImageUrl || '')
    },

    // The tracked subset of the form used for dirty detection (dirtyTracking).
    currentFormState () {
      return {
        title: this.title,
        description: this.description,
        project_type_ids: this.projectTypeIds,
        client_id: this.clientId,
        show_on_website: this.showOnWebsite,
        image_reference: this.imageMode === 'url' ? this.imageUrl : this.initialImageReference,
        image_alt: this.imageAlt
      }
    },

    // Whether the editor holds unsaved changes relative to the saved baseline.
    // A pending upload selection always counts as a change since the file is
    // not part of the tracked baseline fields (Requirements 11.1, 11.4).
    isDirty () {
      const dirtyFields = this.baseline ? isDirty(this.baseline, this.currentFormState) : false
      const pendingUpload = this.imageMode === 'upload' && !!this.imageFile
      return dirtyFields || pendingUpload
    }
  },

  watch: {
    // Surface dirty-state transitions so the container (task 13.4/14.1) can
    // install navigation guards / prompts.
    isDirty (value) {
      if (value !== this.lastDirty) {
        this.lastDirty = value
        this.$emit('dirty-change', value)
      }
    }
  },

  created () {
    this.init()
  },

  mounted () {
    // Warn on a full page unload (refresh / tab close / external navigation)
    // whenever the editor holds unsaved changes (Requirement 11.1). The
    // listener is removed on unmount so it never fires for other views.
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },

  beforeUnmount () {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    this.revokeUploadPreview()
  },

  // In-component navigation guard (the Options API equivalent of
  // onBeforeRouteLeave). Prompts before leaving the editor while it holds
  // unsaved changes: confirming discards the edits and completes the
  // navigation (Requirement 11.2); cancelling stays on the editor with the
  // edits intact (Requirement 11.3). A successful save re-baselines the form
  // (rebaselineAfterSave), so isDirty is false and navigation is not blocked
  // (Requirement 11.4).
  beforeRouteLeave (to, from, next) {
    if (this.isDirty && !this.confirmDiscard()) {
      next(false)
      return
    }
    next()
  },

  methods: {
    async init () {
      await this.loadReferences()
      if (this.isEditMode) {
        await this.loadProject()
      } else {
        // Establish the create-mode baseline once references are loaded.
        this.baseline = captureBaseline(this.currentFormState)
      }
    },

    // Populate the Service_Category selector from `getprojecttypes` and the
    // Client_Record selector from `getclients` (Requirement 4.2). Both go
    // through the JWT-attaching Api_Client via the AdministrationApi service.
    async loadReferences () {
      this.referencesLoading = true
      try {
        const [typesRes, clientsRes] = await Promise.all([
          AdministrationApi.getProjectTypes(),
          AdministrationApi.getClients()
        ])
        this.projectTypes = (typesRes && typesRes.data && typesRes.data.projectTypes) || []
        this.clients = (clientsRes && clientsRes.data && clientsRes.data.clients) || []
      } catch (e) {
        // Selector data is non-fatal for rendering the form; leave lists empty.
        this.projectTypes = this.projectTypes || []
        this.clients = this.clients || []
      } finally {
        this.referencesLoading = false
      }
    },

    // Load an existing project's stored values into the form (Requirement 5.1).
    async loadProject () {
      this.projectLoading = true
      this.loadError = ''
      try {
        const res = await AdminProjectApi.get(this.projectId)
        const project = (res && res.data && (res.data.project || res.data)) || {}
        this.applyProject(project)
        this.baseline = captureBaseline(this.currentFormState)
      } catch (err) {
        this.loadError = extractErrorMessage(err, 'Unable to load the project. Please try again.')
      } finally {
        this.projectLoading = false
      }
    },

    // Map a fetched managed-project record onto the editable fields.
    applyProject (project) {
      this.title = project.title || ''
      this.description = project.description || ''
      this.projectTypeIds = Array.isArray(project.project_type_ids) && project.project_type_ids.length > 0
        ? project.project_type_ids
        : (project.project_type_id != null ? [project.project_type_id] : [])
      this.clientId = project.client_id != null ? project.client_id : null
      this.showOnWebsite = project.show_on_website === true
      this.imageAlt = project.imageAlt || project.image_alt || ''

      const storedRef = project.image_reference || ''
      const resolvedUrl = project.imageUrl || ''
      this.initialImageReference = storedRef
      this.initialImageUrl = resolvedUrl
      this.initialImageAlt = this.imageAlt
      this.hadImageInitially = !!(storedRef || resolvedUrl)

      this.removeRequested = false

      // If the stored reference is a hosted http/https URL, show it in the URL
      // field for editing. If it is an uploaded file (an upload key, not a
      // URL), leave the URL field empty — the image still previews from its
      // resolved served URL (initialImageUrl) and is preserved on save unless
      // explicitly replaced or removed.
      if (isValidHttpUrl(storedRef)) {
        this.imageMode = 'url'
        this.imageUrl = storedRef
      } else {
        this.imageMode = 'url'
        this.imageUrl = ''
      }
    },

    reload () {
      if (this.isEditMode) {
        this.loadProject()
      }
    },

    onFileSelected (file) {
      // v-file-input may emit a File, an array of files, or null.
      const selected = Array.isArray(file) ? file[0] : file
      this.imageFile = selected || null
      // Choosing a file is a new image, not a removal.
      if (this.imageFile) this.removeRequested = false
      this.clearFieldError('image')
      this.revokeUploadPreview()
      if (this.imageFile && typeof URL !== 'undefined' && URL.createObjectURL) {
        this.uploadPreviewUrl = URL.createObjectURL(this.imageFile)
      }
    },

    // URL field input handler: clear any field error and cancel a pending
    // removal once the admin types a URL.
    onImageUrlInput () {
      this.clearFieldError('image')
      if ((this.imageUrl || '').trim() !== '') {
        this.removeRequested = false
      }
    },

    // Explicitly remove the current image. Marks a pending removal (applied on
    // save), drops any pending upload, and clears the URL field so the preview
    // disappears. For an uploaded image this is the only way to remove it,
    // since an empty URL field otherwise means "keep the uploaded image".
    requestRemoveImage () {
      this.removeRequested = true
      this.imageFile = null
      this.revokeUploadPreview()
      this.imageUrl = ''
      this.clearFieldError('image')
    },

    revokeUploadPreview () {
      if (this.uploadPreviewUrl && typeof URL !== 'undefined' && URL.revokeObjectURL) {
        URL.revokeObjectURL(this.uploadPreviewUrl)
      }
      this.uploadPreviewUrl = ''
    },

    clearFieldError (field) {
      if (this.fieldErrors[field]) {
        const next = { ...this.fieldErrors }
        delete next[field]
        this.fieldErrors = next
      }
    },

    // Client-side validation. Populates fieldErrors and returns true when the
    // form is submittable. Blocks the submit and marks the invalid fields
    // without sending a request (Requirements 4.4, 5.3).
    validate () {
      const errors = {}

      const title = (this.title || '').trim()
      if (title.length < 1) {
        errors.title = 'Title is required.'
      } else if (title.length > MAX_TITLE_LENGTH) {
        errors.title = `Title must be at most ${MAX_TITLE_LENGTH} characters.`
      }

      if ((this.description || '').length > MAX_DESCRIPTION_LENGTH) {
        errors.description = `Description must be at most ${MAX_DESCRIPTION_LENGTH} characters.`
      }

      if (!Array.isArray(this.projectTypeIds) || this.projectTypeIds.length === 0) {
        errors.project_type_ids = 'At least one service category is required.'
      }

      if (!isSelected(this.clientId)) {
        errors.client_id = 'A client is required.'
      }

      if (this.imageMode === 'url') {
        const url = (this.imageUrl || '').trim()
        if (url !== '' && !isValidHttpUrl(url)) {
          errors.image = `Image URL must be a well-formed http or https URL of at most ${MAX_IMAGE_URL_LENGTH} characters.`
        }
      }

      this.fieldErrors = errors
      return Object.keys(errors).length === 0
    },

    async save () {
      this.saveError = ''
      // Block invalid submissions before any request is sent.
      if (!this.validate()) {
        return
      }

      this.saving = true
      try {
        const payload = {
          title: (this.title || '').trim(),
          description: this.description || '',
          project_type_ids: this.projectTypeIds,
          client_id: this.clientId,
          show_on_website: this.showOnWebsite === true
        }

        let project
        if (this.isEditMode) {
          const res = await AdminProjectApi.update(this.projectId, payload)
          project = (res && res.data && (res.data.project || res.data)) || {}
        } else {
          const res = await AdminProjectApi.create(payload)
          project = (res && res.data && (res.data.project || res.data)) || {}
        }

        const savedId = project.id != null ? project.id : this.projectId
        await this.persistImage(savedId, project)

        // Successful save clears the unsaved-changes state so navigation no
        // longer prompts (Requirement 11.4).
        this.rebaselineAfterSave(project)
        this.$emit('saved', project)
      } catch (err) {
        // Preserve entered values; surface the server's message for correction
        // (Requirements 4.6, 5.6).
        this.saveError = extractErrorMessage(err, 'Unable to save the project. Please try again.')
        const fields = err && err.response && err.response.data && err.response.data.fields
        if (fields && typeof fields === 'object') {
          this.fieldErrors = { ...this.fieldErrors, ...fields }
        }
        this.$emit('save-error', this.saveError)
      } finally {
        this.saving = false
      }
    },

    // Persist any image change through the AdminProjectApi. A pending upload is
    // sent as multipart form data; a hosted URL is sent as JSON; clearing the
    // URL on a project that had an image removes it (Requirement 7).
    async persistImage (id, project) {
      if (id == null) return
      const alt = (this.imageAlt || '').trim()

      // 1. A new file was chosen: set or replace the image via upload. The
      //    server deletes any previously stored upload it replaces.
      if (this.imageMode === 'upload' && this.imageFile) {
        const formData = new FormData()
        formData.append('image', this.imageFile)
        formData.append('imageAlt', alt)
        const saved = await AdminProjectApi.setImage(id, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.captureSavedImage(saved, project)
        return
      }

      const url = this.imageMode === 'url' ? (this.imageUrl || '').trim() : ''

      // 2. Explicit removal of the existing image.
      if (this.removeRequested && url === '') {
        if (this.hadImageInitially) {
          await AdminProjectApi.removeImage(id)
          this.hadImageInitially = false
          this.initialImageReference = ''
          this.initialImageUrl = ''
        }
        return
      }

      // 3. A hosted URL was entered or changed (or its alt text changed).
      if (url !== '') {
        const storedIsExternal = isValidHttpUrl(this.initialImageReference)
        const urlChanged = !storedIsExternal || url !== (this.initialImageReference || '')
        const altChanged = alt !== (this.initialImageAlt || '')
        if (urlChanged || altChanged) {
          const saved = await AdminProjectApi.setImage(id, { imageUrl: url, imageAlt: alt })
          this.captureSavedImage(saved, project)
        }
        return
      }

      // 4. Otherwise no image change was requested. An uploaded image with an
      //    empty URL field is preserved (not removed) so unrelated edits (e.g.
      //    the title) never delete the stored image.
    },

    // Adopt the server's resolved image state after a successful image write so
    // the baseline reflects what is now stored.
    captureSavedImage (response, project) {
      const data = (response && response.data && (response.data.project || response.data)) || project || {}
      const resolvedUrl = data.imageUrl || (this.imageMode === 'url' ? (this.imageUrl || '').trim() : this.initialImageUrl)
      this.initialImageReference = data.image_reference || resolvedUrl || ''
      this.initialImageUrl = resolvedUrl || ''
      this.initialImageAlt = (this.imageAlt || '').trim()
      this.hadImageInitially = !!this.initialImageReference
    },

    // Reset the dirty baseline (and clear a consumed upload selection) after a
    // successful save so the editor reports no unsaved changes.
    rebaselineAfterSave (project) {
      this.removeRequested = false
      // Only a hosted (http/https) reference is shown in the URL field; an
      // uploaded image leaves the field empty and previews via initialImageUrl.
      // Never place an internal served path (/uploads/...) in the URL field, or
      // it would fail the http/https validation on the next save.
      const storedIsExternal = isValidHttpUrl(this.initialImageReference)
      if (this.imageMode === 'upload') {
        this.imageFile = null
        this.revokeUploadPreview()
        this.imageMode = 'url'
      }
      this.imageUrl = storedIsExternal ? (this.initialImageReference || '') : ''
      this.initialImageAlt = (this.imageAlt || '').trim()
      if (project && project.id != null) {
        this.hadImageInitially = !!(this.initialImageReference || this.initialImageUrl)
      }
      this.baseline = captureBaseline(this.currentFormState)
    },

    cancel () {
      this.$emit('cancel')
      // Return to the Project_List (Requirement 1.2). The editor is reached
      // via the admin-project-new / admin-project-edit routes, both nested
      // under /admin, so this always resolves relative to the Admin_Shell.
      // beforeRouteLeave (confirmDiscard) still guards this navigation when
      // the form is dirty, so an unsaved edit is not silently discarded.
      this.$router.push({ name: 'admin-projects' })
    },

    // Prompt the admin that unsaved changes will be lost, requiring explicit
    // confirmation before proceeding (Requirement 11.1). Returns true when the
    // admin confirms leaving (discard), false when they cancel (stay).
    confirmDiscard () {
      if (typeof window === 'undefined' || typeof window.confirm !== 'function') {
        return true
      }
      return window.confirm(
        'You have unsaved changes that will be lost. Are you sure you want to leave?'
      )
    },

    // beforeunload handler for full page unloads. Setting returnValue triggers
    // the browser's native confirmation dialog; it only fires while the editor
    // is dirty so a clean form never blocks a refresh (Requirements 11.1, 11.4).
    handleBeforeUnload (event) {
      if (!this.isDirty) return undefined
      event.preventDefault()
      // Some browsers require returnValue to be set to show the prompt.
      event.returnValue = ''
      return ''
    }
  }
}
</script>

<style scoped>
.project-editor__title {
  font-weight: 700;
  color: #41281b;
}

.project-editor__preview-img {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
</style>
